// src/views/saas/ClientesList.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import saasService from "../../services/saasService";
import Banner from "../../components/ui/Banner";

const fmtD   = d => { try{ const s=(d||"").split("T")[0]; return s?new Date(s+"T12:00:00").toLocaleDateString("pt-BR"):"—"; }catch{ return "—"; } };
const fmtM   = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const STATUS_COLOR = {
  Ativo:       {bg:"#DCFCE7",color:"#166534"},
  GracePeriod: {bg:"#FEF9C3",color:"#854D0E"},
  Bloqueado:   {bg:"#FEE2E2",color:"#991B1B"},
  Cancelado:   {bg:"#F1F5F9",color:"var(--gray-600)"},
};


// ── Validações CPF / CNPJ ─────────────────────────────────────────────────
function validarCPF(cpf) {
  const s = cpf.replace(/\D/g, "");
  if (s.length !== 11 || /^(\d)\1{10}$/.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(s[i]) * (10 - i);
  let r = (sum * 10) % 11; if (r === 10 || r === 11) r = 0;
  if (r !== parseInt(s[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(s[i]) * (11 - i);
  r = (sum * 10) % 11; if (r === 10 || r === 11) r = 0;
  return r === parseInt(s[10]);
}
function validarCNPJ(cnpj) {
  const s = cnpj.replace(/\D/g, "");
  if (s.length !== 14 || /^(\d)\1{13}$/.test(s)) return false;
  const calc = (str, w) => {
    let sum = 0;
    for (let i = 0; i < w.length; i++) sum += parseInt(str[i]) * w[i];
    const r = sum % 11; return r < 2 ? 0 : 11 - r;
  };
  return calc(s,[5,4,3,2,9,8,7,6,5,4,3,2]) === parseInt(s[12]) &&
         calc(s,[6,5,4,3,2,9,8,7,6,5,4,3,2]) === parseInt(s[13]);
}
function validarCpfCnpj(valor, tpPessoa) {
  if (!valor) return null;
  const digits = valor.replace(/\D/g, "");
  if (tpPessoa === "Fisica") {
    if (digits.length !== 11) return "CPF deve ter 11 dígitos.";
    if (!validarCPF(valor)) return "CPF inválido. Verifique os dígitos.";
  } else {
    if (digits.length !== 14) return "CNPJ deve ter 14 dígitos.";
    if (!validarCNPJ(valor)) return "CNPJ inválido. Verifique os dígitos.";
  }
  return null;
}
function aplicarMascara(valor, tpPessoa) {
  const d = valor.replace(/\D/g, "");
  if (tpPessoa === "Fisica") {
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4").substring(0, 14);
  }
  return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5").substring(0, 18);
}

const EMPTY_FORM = {
  nmCliente:"", tpPessoa:"Juridica", cpf_cnpj:"", email:"",
  telefone:"", celular:"", responsavel:"", idPlano:"",
  dtVencimento:"", gracePeriodDias:"5", observacao:"", logo:null,
  nomeAdmin:"", emailAdmin:"", senhaAdmin:""
};

export default function ClientesList() {
  const [lista, setLista]         = useState([]);
  const [planos, setPlanos]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroPlano, setFiltroPlano]   = useState("");
  const [filtroVenc, setFiltroVenc]     = useState(""); // vencidos|prox7|prox15
  const [ordenacao, setOrdenacao]       = useState("nome"); // nome|venc|criado|condominios
  const [selected, setSelected]   = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showVenc, setShowVenc]   = useState(false);
  const [editData, setEditData]   = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [statusForm, setStatusForm] = useState({status:"",motivo:""});
  const [novaVenc, setNovaVenc]   = useState({dtVencimento:"",motivo:""});
  const [saving, setSaving]       = useState(false);
  const [cpfCnpjErro, setCpfCnpjErro] = useState("");
  const [banner, setBanner]       = useState(null);
  const notify = (mensagem, tipo="info") => setBanner({ mensagem, tipo });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await saasService.listarClientes({search, status:filtroStatus, limit:50});
      setLista(r.data||[]);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [search, filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    saasService.listarPlanos().then(r=>setPlanos(r.data||[])).catch(()=>{});
  }, []);

  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  // Filtros e ordenação aplicados em memória
  const listaFiltrada = useMemo(() => {
    let r = [...lista];
    if (filtroPlano) r = r.filter(c => String(c.idPlano||"") === String(filtroPlano));
    if (filtroVenc === "vencidos")  r = r.filter(c => c.diasVencimento != null && c.diasVencimento < 0);
    if (filtroVenc === "prox7")     r = r.filter(c => c.diasVencimento != null && c.diasVencimento >= 0 && c.diasVencimento <= 7);
    if (filtroVenc === "prox15")    r = r.filter(c => c.diasVencimento != null && c.diasVencimento >= 0 && c.diasVencimento <= 15);

    switch (ordenacao) {
      case "venc":         r.sort((a,b)=>{ const ax=a.diasVencimento??99999, bx=b.diasVencimento??99999; return ax-bx; }); break;
      case "criado":       r.sort((a,b)=>new Date(b.createdAt||0)-new Date(a.createdAt||0)); break;
      case "condominios":  r.sort((a,b)=>(b.qtdCondominios||0)-(a.qtdCondominios||0)); break;
      default:             r.sort((a,b)=>(a.nmCliente||"").localeCompare(b.nmCliente||""));
    }
    return r;
  }, [lista, filtroPlano, filtroVenc, ordenacao]);

  // Indicadores rápidos no topo
  const kpis = useMemo(() => {
    const total = lista.length;
    const ativos = lista.filter(c=>c.status==="Ativo").length;
    const grace = lista.filter(c=>c.status==="GracePeriod").length;
    const bloq  = lista.filter(c=>c.status==="Bloqueado").length;
    const venc  = lista.filter(c=>c.diasVencimento!=null && c.diasVencimento<0 && c.status!=="Cancelado").length;
    const prox7 = lista.filter(c=>c.diasVencimento!=null && c.diasVencimento>=0 && c.diasVencimento<=7).length;
    return { total, ativos, grace, bloq, venc, prox7 };
  }, [lista]);

  const handleSalvar = async () => {
    const cpfCnpjMsg = validarCpfCnpj(form.cpf_cnpj, form.tpPessoa);
    if (cpfCnpjMsg) { setCpfCnpjErro(cpfCnpjMsg); return; }
    setCpfCnpjErro("");
    setSaving(true);
    try {
      if (editData?.id) await saasService.atualizarCliente(editData.id, form);
      else              await saasService.criarCliente(form);
      setShowModal(false); setCpfCnpjErro(''); carregar();
      notify(editData?.id?"Cliente atualizado com sucesso.":"Cliente criado com sucesso.", "success");
    } catch(e) { notify(e.message || "Erro ao salvar cliente.", "danger"); }
    setSaving(false);
  };

  const handleAlterarStatus = async () => {
    setSaving(true);
    try {
      await saasService.alterarStatus(selected.id, statusForm);
      setShowStatus(false);
      await carregar();
      try { const r = await saasService.buscarCliente(selected.id); setSelected(r.data); } catch {}
      notify(`Status alterado para ${statusForm.status}.`, "success");
    } catch(e) { notify(e.message || "Erro ao alterar status.", "danger"); }
    setSaving(false);
  };

  const handleEstenderVencimento = async () => {
    if (!novaVenc.dtVencimento) { notify("Informe a nova data de vencimento.", "warning"); return; }
    setSaving(true);
    try {
      await saasService.estenderVencimento(selected.id, novaVenc);
      setShowVenc(false);
      setNovaVenc({dtVencimento:"",motivo:""});
      await carregar();
      try { const r = await saasService.buscarCliente(selected.id); setSelected(r.data); } catch {}
      notify("Vencimento atualizado com sucesso.", "success");
    } catch(e) { notify(e.message || "Erro ao atualizar vencimento.", "danger"); }
    setSaving(false);
  };

  const abrirDetalhe = async (c) => {
    try {
      const r = await saasService.buscarCliente(c.id);
      setSelected(r.data);
    } catch(e) { setSelected(c); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-people-fill me-2" style={{color:"var(--primary)",fontSize:22}}/>
            Clientes SaaS
          </h1>
          <p className="page-subtitle">Gerencie os clientes e licenças da plataforma</p>
        </div>
        <button className="btn btn-primary" onClick={()=>{setEditData(null);setForm(EMPTY_FORM);setShowModal(true);}}>
          <i className="bi bi-plus-lg me-1"/>Novo Cliente
        </button>
      </div>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10,marginBottom:14}}>
        {[
          {l:"Total",        v:kpis.total, bg:"#EFF6FF", c:"#1D4ED8", filtro:()=>{setFiltroStatus("");setFiltroVenc("");}},
          {l:"Ativos",       v:kpis.ativos, bg:"#DCFCE7", c:"#166534", filtro:()=>setFiltroStatus("Ativo")},
          {l:"Grace Period", v:kpis.grace, bg:"#FEF9C3", c:"#854D0E", filtro:()=>setFiltroStatus("GracePeriod")},
          {l:"Bloqueados",   v:kpis.bloq,  bg:"#FEE2E2", c:"#991B1B", filtro:()=>setFiltroStatus("Bloqueado")},
          {l:"Vencidos",     v:kpis.venc,  bg:"#FECACA", c:"#7F1D1D", filtro:()=>setFiltroVenc("vencidos")},
          {l:"Vence 7d",     v:kpis.prox7, bg:"#FEF3C7", c:"#92400E", filtro:()=>setFiltroVenc("prox7")},
        ].map(k=>(
          <button key={k.l} onClick={k.filtro}
            style={{textAlign:"left",border:"1px solid var(--gray-200)",borderRadius:10,
              padding:"10px 12px",background:k.bg,cursor:"pointer"}}>
            <div style={{fontSize:11,color:k.c,fontWeight:600}}>{k.l}</div>
            <div style={{fontSize:20,fontWeight:800,color:k.c}}>{k.v}</div>
          </button>
        ))}
      </div>

      {/* Filtros — usa CSS Grid com minmax(0,Xfr) para garantir inline mesmo
          quando o Bootstrap aplica width:100% nos campos */}
      <div className="card" style={{padding:"10px 14px", marginBottom:16}}>
        <div style={{
          display:"grid",
          gridTemplateColumns:
            "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto",
          gap:10,
          alignItems:"center",
        }}>
          <input className="form-control"
            style={{fontSize:13, minWidth:0}}
            placeholder="🔍 Buscar por nome, email, CNPJ..."
            value={search} onChange={e=>setSearch(e.target.value)}/>

          <select className="form-select"
            style={{fontSize:13, minWidth:0}}
            value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="Ativo">Ativo</option>
            <option value="GracePeriod">Grace Period</option>
            <option value="Bloqueado">Bloqueado</option>
            <option value="Cancelado">Cancelado</option>
          </select>

          <select className="form-select"
            style={{fontSize:13, minWidth:0}}
            value={filtroPlano} onChange={e=>setFiltroPlano(e.target.value)}>
            <option value="">Todos os planos</option>
            {planos.map(p=><option key={p.id} value={p.id}>{p.nmPlano}</option>)}
          </select>

          <select className="form-select"
            style={{fontSize:13, minWidth:0}}
            value={filtroVenc} onChange={e=>setFiltroVenc(e.target.value)}>
            <option value="">Qualquer vencimento</option>
            <option value="vencidos">Vencidos</option>
            <option value="prox7">Vencem em 7 dias</option>
            <option value="prox15">Vencem em 15 dias</option>
          </select>

          <select className="form-select"
            style={{fontSize:13, minWidth:0}}
            value={ordenacao} onChange={e=>setOrdenacao(e.target.value)}>
            <option value="nome">Ordem: Nome</option>
            <option value="venc">Ordem: Próximo vencimento</option>
            <option value="criado">Ordem: Mais recente</option>
            <option value="condominios">Ordem: Mais condomínios</option>
          </select>

          {/* Coluna auto: limpar + contador */}
          <div style={{display:"flex", gap:8, alignItems:"center", whiteSpace:"nowrap"}}>
            {(search||filtroStatus||filtroPlano||filtroVenc) && (
              <button className="btn btn-outline-secondary btn-sm"
                onClick={()=>{setSearch("");setFiltroStatus("");setFiltroPlano("");setFiltroVenc("");}}>
                <i className="bi bi-x-circle me-1"/>Limpar
              </button>
            )}
            <span style={{fontSize:12, color:"var(--gray-500)"}}>
              {listaFiltrada.length} de {lista.length}
            </span>
          </div>
        </div>
      </div>

      <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
        {/* Lista */}
        <div style={{flex:1}}>
          <div className="card" style={{overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{background:"var(--gray-50)"}}>
                  {["Cliente","Plano","Vencimento","Condomínios","Usuários","Status",""].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",
                      fontWeight:600,color:"var(--gray-600)",fontSize:12}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{textAlign:"center",padding:40}}>
                    <span className="spinner-border text-primary"/>
                  </td></tr>
                ) : !listaFiltrada.length ? (
                  <tr><td colSpan={7} style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                    {lista.length ? "Nenhum cliente atende aos filtros selecionados." : "Nenhum cliente cadastrado"}
                  </td></tr>
                ) : listaFiltrada.map(c=>(
                  <tr key={c.id} onClick={()=>abrirDetalhe(c)}
                    style={{borderBottom:"1px solid var(--gray-100)",cursor:"pointer",
                      background:selected?.id===c.id?"#EFF6FF":"#fff"}}
                    onMouseEnter={e=>e.currentTarget.style.background=selected?.id===c.id?"#EFF6FF":"#F8FAFC"}
                    onMouseLeave={e=>e.currentTarget.style.background=selected?.id===c.id?"#EFF6FF":"#fff"}>
                    <td style={{padding:"10px 14px"}}>
                      <div style={{fontWeight:700,color:"var(--gray-800)"}}>{c.nmCliente}</div>
                      <div style={{fontSize:11,color:"var(--gray-400)"}}>{c.email}</div>
                    </td>
                    <td style={{padding:"10px 14px"}}>
                      <span style={{fontSize:12,fontWeight:600,color:"var(--primary)"}}>
                        {c.nmPlano||"—"}
                      </span>
                      {c.preco>0 && <div style={{fontSize:11,color:"var(--gray-400)"}}>{fmtM(c.preco)}/{c.periodo==="Mensal"?"mês":c.periodo?.toLowerCase()||"mês"}</div>}
                    </td>
                    <td style={{padding:"10px 14px",fontSize:12}}>
                      {c.dtVencimento ? (
                        <span style={{color:c.diasVencimento<0?"#991B1B":c.diasVencimento<7?"#854D0E":"var(--gray-700)"}}>
                          {fmtD(c.dtVencimento)}
                          {c.diasVencimento<0 && <span style={{marginLeft:4,fontSize:10,color:"#991B1B"}}>({Math.abs(c.diasVencimento)}d vencido)</span>}
                          {c.diasVencimento>=0 && c.diasVencimento<7 && <span style={{marginLeft:4,fontSize:10,color:"#854D0E"}}>({c.diasVencimento}d)</span>}
                        </span>
                      ) : <span style={{color:"var(--gray-400)"}}>Sem vencimento</span>}
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"center"}}>{c.qtdCondominios}</td>
                    <td style={{padding:"10px 14px",textAlign:"center"}}>{c.qtdUsuarios}</td>
                    <td style={{padding:"10px 14px"}}>
                      <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
                        ...STATUS_COLOR[c.status]}}>
                        {c.status==="GracePeriod"?"⚠️ Grace Period":c.status}
                      </span>
                    </td>
                    <td style={{padding:"10px 14px"}} onClick={e=>e.stopPropagation()}>
                      <div style={{display:"flex",gap:4}}>
                        <button className="btn btn-outline-secondary btn-sm btn-icon" title="Editar"
                          onClick={()=>{
                            setEditData(c);
                            setForm({...EMPTY_FORM, ...c,senhaAdmin:"",nomeAdmin:"",emailAdmin:"",
                              dtVencimento:c.dtVencimento?String(c.dtVencimento).split("T")[0]:""});
                            setShowModal(true);
                          }}>
                          <i className="bi bi-pencil"/>
                        </button>
                        <button className="btn btn-outline-secondary btn-sm btn-icon" title="Estender vencimento"
                          onClick={()=>{setSelected(c);setNovaVenc({dtVencimento:"",motivo:""});setShowVenc(true);}}>
                          <i className="bi bi-calendar-plus"/>
                        </button>
                        <button className="btn btn-sm btn-icon" title={c.status==="Ativo"?"Inativar/Bloquear":"Reativar"}
                          style={{
                            background:c.status==="Ativo"?"#FEE2E2":"#DCFCE7",
                            color:c.status==="Ativo"?"#991B1B":"#166534",
                            border:"none",borderRadius:6,width:28,height:28,cursor:"pointer",
                            display:"flex",alignItems:"center",justifyContent:"center"
                          }}
                          onClick={()=>{setSelected(c);setStatusForm({status:c.status==="Ativo"?"Bloqueado":"Ativo",motivo:""});setShowStatus(true);}}>
                          <i className={`bi bi-${c.status==="Ativo"?"slash-circle":"check-circle"}`}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Painel de detalhe */}
        {selected && (
          <div style={{width:340,flexShrink:0}}>
            <div className="card">
              <div style={{padding:"14px 16px",borderBottom:"1px solid var(--gray-100)",
                background:"var(--primary)",borderRadius:"12px 12px 0 0",
                display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{color:"#fff",fontWeight:700,fontSize:14}}>{selected.nmCliente}</div>
                  <div style={{color:"rgba(255,255,255,.7)",fontSize:12}}>{selected.email}</div>
                </div>
                <button className="btn-close btn-sm" style={{filter:"invert(1)"}}
                  onClick={()=>setSelected(null)}/>
              </div>
              <div style={{padding:16}}>
                {/* Status badge */}
                <div style={{textAlign:"center",marginBottom:16}}>
                  <span style={{fontSize:13,fontWeight:700,padding:"6px 18px",borderRadius:20,
                    ...STATUS_COLOR[selected.status]}}>
                    {selected.status==="GracePeriod"?"⚠️ Em Grace Period":selected.status}
                  </span>
                </div>
                {/* Info */}
                {[
                  ["Plano",         selected.nmPlano||"Sem plano"],
                  ["Responsável",   selected.responsavel||"—"],
                  ["Telefone",      selected.celular||selected.telefone||"—"],
                  ["Contratação",   fmtD(selected.dtContratacao)],
                  ["Vencimento",    fmtD(selected.dtVencimento)||"Sem vencimento"],
                  ["Grace Period",  `${selected.gracePeriodDias} dias`],
                  ["Condomínios",   `${selected.qtdCondominios||0} / ${selected.maxCondominios||"∞"}`],
                  ["Usuários",      `${selected.qtdUsuarios||0} / ${selected.maxUsuarios||"∞"}`],
                ].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",
                    padding:"5px 0",borderBottom:"1px solid var(--gray-100)",fontSize:13}}>
                    <span style={{color:"var(--gray-500)"}}>{l}</span>
                    <span style={{fontWeight:600,color:"var(--gray-800)"}}>{v}</span>
                  </div>
                ))}

                {/* Ações */}
                <div style={{marginTop:16,display:"flex",flexDirection:"column",gap:8}}>
                  <button className="btn btn-outline-secondary btn-sm" onClick={()=>setShowVenc(true)}>
                    <i className="bi bi-calendar-check me-1"/>Estender Vencimento
                  </button>
                  {selected.status!=="Ativo" ? (
                    <button className="btn btn-success btn-sm"
                      onClick={()=>{setStatusForm({status:"Ativo",motivo:"Regularização de pagamento"});setShowStatus(true);}}>
                      <i className="bi bi-check-circle me-1"/>Reativar Acesso
                    </button>
                  ) : (
                    <button className="btn btn-outline-danger btn-sm"
                      onClick={()=>{setStatusForm({status:"Bloqueado",motivo:""});setShowStatus(true);}}>
                      <i className="bi bi-slash-circle me-1"/>Bloquear Acesso
                    </button>
                  )}
                </div>

                {/* Condomínios */}
                {(selected.condominios||[]).length > 0 && (
                  <div style={{marginTop:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:"var(--gray-600)",
                      marginBottom:8,textTransform:"uppercase",letterSpacing:".3px"}}>
                      Condomínios
                    </div>
                    {(selected.condominios||[]).map(co=>(
                      <div key={co.id} style={{fontSize:12,padding:"4px 0",
                        color:"var(--gray-700)",borderBottom:"1px solid var(--gray-100)"}}>
                        <i className="bi bi-buildings me-1" style={{color:"var(--primary)"}}/>
                        {co.nmFantasia||co.nmCondominio}
                      </div>
                    ))}
                  </div>
                )}

                {/* Histórico */}
                {(selected.historico||[]).length > 0 && (
                  <div style={{marginTop:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:"var(--gray-600)",
                      marginBottom:8,textTransform:"uppercase",letterSpacing:".3px"}}>
                      Histórico Recente
                    </div>
                    {(selected.historico||[]).slice(0,5).map(h=>(
                      <div key={h.id} style={{fontSize:11,padding:"4px 0",
                        borderBottom:"1px solid var(--gray-100)",color:"var(--gray-600)"}}>
                        <span style={{fontWeight:600}}>{h.tpAcao}</span> — {h.descricao}
                        <div style={{fontSize:10,color:"var(--gray-400)"}}>
                          {new Date(h.createdAt).toLocaleDateString("pt-BR")} — {h.executadoPor}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Criar/Editar */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{maxWidth:640,maxHeight:"calc(100vh - 60px)"}}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-person-badge me-2"/>
                {editData?.id?"Editar Cliente":"Novo Cliente"}
              </h5>
              <button className="btn-close" onClick={()=>setShowModal(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-8">
                  <label className="form-label">Razão Social / Nome *</label>
                  <input className="form-control" value={form.nmCliente}
                    onChange={e=>set("nmCliente",e.target.value)} placeholder="Nome do cliente"/>
                </div>
                <div className="col-4">
                  <label className="form-label fw-semibold">Tipo de Pessoa</label>
                  <div style={{display:"flex",gap:6}}>
                    {["Juridica","Fisica"].map(tp=>(
                      <button key={tp} type="button"
                        onClick={()=>{set("tpPessoa",tp);set("cpf_cnpj","");setCpfCnpjErro("");}}
                        style={{
                          flex:1,padding:"7px 4px",border:"2px solid",
                          borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,
                          transition:"all .15s",
                          borderColor:  form.tpPessoa===tp?"#1B3A6B":"#E2E8F0",
                          background:   form.tpPessoa===tp?"#1B3A6B":"#fff",
                          color:        form.tpPessoa===tp?"#fff":"#64748B",
                        }}>
                        {tp==="Juridica"?"🏢 CNPJ":"👤 CPF"}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-6">
                  <label className="form-label">
                    {form.tpPessoa==="Fisica" ? "CPF" : "CNPJ"}
                  </label>
                  <input
                    className={`form-control ${cpfCnpjErro?"is-invalid":form.cpf_cnpj&&!cpfCnpjErro?"is-valid":""}`}
                    value={form.cpf_cnpj}
                    placeholder={form.tpPessoa==="Fisica" ? "000.000.000-00" : "00.000.000/0000-00"}
                    maxLength={form.tpPessoa==="Fisica" ? 14 : 18}
                    onChange={e=>{
                      const raw = e.target.value;
                      const digits = raw.replace(/\D/g,"");
                      // Auto-detect type: CPF=11 digits, CNPJ=14
                      const autoTp = digits.length <= 11 ? "Fisica" : "Juridica";
                      if (autoTp !== form.tpPessoa) set("tpPessoa", autoTp);
                      const masked = aplicarMascara(raw, autoTp);
                      set("cpf_cnpj", masked);
                      const expectedLen = autoTp==="Fisica" ? 11 : 14;
                      if (digits.length === expectedLen) {
                        setCpfCnpjErro(validarCpfCnpj(masked, autoTp) || "");
                      } else {
                        setCpfCnpjErro("");
                      }
                    }}
                    onBlur={()=>{
                      if (form.cpf_cnpj) setCpfCnpjErro(validarCpfCnpj(form.cpf_cnpj, form.tpPessoa) || "");
                    }}
                  />
                  {cpfCnpjErro && <div className="invalid-feedback">{cpfCnpjErro}</div>}
                  {!cpfCnpjErro && form.cpf_cnpj && (
                    <div className="valid-feedback">
                      {form.tpPessoa==="Fisica" ? "CPF válido ✓" : "CNPJ válido ✓"}
                    </div>
                  )}
                </div>
                <div className="col-6">
                  <label className="form-label">Email *</label>
                  <input className="form-control" type="email" value={form.email} onChange={e=>set("email",e.target.value)}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Telefone</label>
                  <input className="form-control" value={form.telefone} onChange={e=>set("telefone",e.target.value)}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Responsável</label>
                  <input className="form-control" value={form.responsavel} onChange={e=>set("responsavel",e.target.value)}/>
                </div>
                <div className="col-6">
                  <label className="form-label">Plano</label>
                  <select className="form-select" value={form.idPlano} onChange={e=>set("idPlano",e.target.value)}>
                    <option value="">Selecione o plano</option>
                    {planos.map(p=><option key={p.id} value={p.id}>{p.nmPlano} — {fmtM(p.preco)}/mês</option>)}
                  </select>
                </div>
                <div className="col-4">
                  <label className="form-label">Vencimento</label>
                  <input type="date" className="form-control" value={form.dtVencimento}
                    onChange={e=>set("dtVencimento",e.target.value)}/>
                </div>
                <div className="col-2">
                  <label className="form-label">Grace (dias)</label>
                  <input type="number" className="form-control" value={form.gracePeriodDias}
                    onChange={e=>set("gracePeriodDias",e.target.value)} min={0} max={30}/>
                </div>
                {!editData?.id && (<>
                  <div className="col-12">
                    <hr style={{borderColor:"var(--gray-200)",margin:"4px 0"}}/>
                    <div style={{fontSize:12,fontWeight:700,color:"var(--gray-600)",textTransform:"uppercase"}}>
                      Usuário Administrador do Cliente
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Nome do Admin</label>
                    <input className="form-control" value={form.nomeAdmin}
                      onChange={e=>set("nomeAdmin",e.target.value)} placeholder="Nome completo"/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Email do Admin</label>
                    <input className="form-control" type="email" value={form.emailAdmin}
                      onChange={e=>set("emailAdmin",e.target.value)} placeholder="email@admin.com"/>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Senha *</label>
                    <input className="form-control" type="password" value={form.senhaAdmin}
                      autoComplete="new-password"
                      onChange={e=>set("senhaAdmin",e.target.value)}/>
                  </div>
                </>)}
                <div className="col-12">
                  <label className="form-label">Observação</label>
                  <textarea className="form-control" rows={2} value={form.observacao}
                    onChange={e=>set("observacao",e.target.value)}/>
                </div>
                <div className="col-12">
                  <label className="form-label">Logo do Cliente</label>
                  <div style={{display:"flex",alignItems:"center",gap:16}}>
                    {form.logo && (
                      <img src={form.logo} alt="Logo" style={{height:60,maxWidth:160,objectFit:"contain",border:"1px solid var(--border-color)",borderRadius:8,padding:4,background:"var(--bg-surface)"}}/>
                    )}
                    <div>
                      <input type="file" accept="image/*" className="form-control form-control-sm" style={{width:"auto"}}
                        onChange={e=>{
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 500*1024) { notify("Logo muito grande. Máximo 500KB.", "warning"); return; }
                          const reader = new FileReader();
                          reader.onload = () => set("logo", reader.result);
                          reader.readAsDataURL(file);
                        }}/>
                      <small style={{fontSize:11,color:"var(--gray-400)"}}>JPG/PNG até 500KB. Aparece no Portal do Condômino e emails.</small>
                    </div>
                    {form.logo && (
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={()=>set("logo",null)}>
                        <i className="bi bi-trash"/>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSalvar} disabled={saving}>
                {saving?<><span className="spinner-border spinner-border-sm me-1"/>Salvando...</>:"Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Status */}
      {showStatus && (
        <div className="modal-overlay">
          <div className="modal-box" style={{maxWidth:440}}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className={`bi bi-${statusForm.status==="Ativo"?"check-circle":"slash-circle"} me-2`}/>
                {statusForm.status==="Ativo"?"Reativar Acesso":"Bloquear Acesso"}
              </h5>
              <button className="btn-close" onClick={()=>setShowStatus(false)}/>
            </div>
            <div className="modal-body">
              <p style={{fontSize:13}}>
                {statusForm.status==="Ativo"
                  ? `Confirma a reativação do acesso para ${selected?.nmCliente}?`
                  : `Confirma o bloqueio de ${selected?.nmCliente}? O cliente não conseguirá fazer login.`}
              </p>
              <label className="form-label">Motivo</label>
              <input className="form-control" value={statusForm.motivo}
                onChange={e=>setStatusForm(p=>({...p,motivo:e.target.value}))}
                placeholder={statusForm.status==="Ativo"?"Ex: Pagamento confirmado":"Ex: Fatura vencida"}/>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowStatus(false)}>Cancelar</button>
              <button
                className={`btn btn-${statusForm.status==="Ativo"?"success":"danger"}`}
                onClick={handleAlterarStatus} disabled={saving}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banner global de notificação */}
      {banner && (
        <div style={{
          position:"fixed", top:70, left:0, right:0,
          zIndex:1100, display:"flex", justifyContent:"center",
          pointerEvents:"none", padding:"0 16px"
        }}>
          <div style={{ maxWidth:520, width:"100%", pointerEvents:"auto" }}>
            <Banner tipo={banner.tipo} onClose={()=>setBanner(null)}>
              {banner.mensagem}
            </Banner>
          </div>
        </div>
      )}

      {/* Modal Vencimento */}
      {showVenc && (
        <div className="modal-overlay">
          <div className="modal-box" style={{maxWidth:440}}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-calendar-check me-2"/>Estender Vencimento</h5>
              <button className="btn-close" onClick={()=>setShowVenc(false)}/>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Nova Data de Vencimento</label>
                  <input type="date" className="form-control" value={novaVenc.dtVencimento}
                    onChange={e=>setNovaVenc(p=>({...p,dtVencimento:e.target.value}))}/>
                </div>
                <div className="col-12">
                  <label className="form-label">Motivo</label>
                  <input className="form-control" value={novaVenc.motivo}
                    onChange={e=>setNovaVenc(p=>({...p,motivo:e.target.value}))}
                    placeholder="Ex: Pagamento confirmado em 15/04"/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={()=>setShowVenc(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleEstenderVencimento} disabled={saving}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
